// services/productCourierRules.ts — aturan kurir per produk (blacklist + wajib drop-off)
// Disimpan di products.attributes.courierRules. null/[] = semua kurir tampil (backward-compat).
// Kode = logistic_code Everpro (lowercase): mis. "jne", "sicepat", "jnt", "ninja".
import { supabase } from '../supabase/client';

export interface ProductCourierRules {
  blockedLogistics: string[];
  dropOnlyLogistics: string[];
}

const cleanCodes = (v: unknown): string[] =>
  Array.isArray(v)
    ? [...new Set(v.filter(x => typeof x === 'string' && x.trim()).map(x => (x as string).trim().toLowerCase()))]
    : [];

export function normalizeCourierRules(raw: unknown): ProductCourierRules | null {
  if (!raw || typeof raw !== 'object') return null;
  const blocked = cleanCodes((raw as any).blockedLogistics);
  const dropOnly = cleanCodes((raw as any).dropOnlyLogistics).filter(c => !blocked.includes(c));
  if (!blocked.length && !dropOnly.length) return null;
  return { blockedLogistics: blocked, dropOnlyLogistics: dropOnly };
}

export async function fetchProductCourierRules(productId?: string | null): Promise<ProductCourierRules | null> {
  if (!productId) return null;
  // RPC dulu (anon-safe: products SELECT ditolak RLS utk publik → body []).
  // Fallback direct-select utk konteks authenticated.
  try {
    const { data, error } = await supabase.rpc('get_product_courier_rules', { p_product_id: productId });
    if (!error && data !== null && data !== undefined) {
      const rules = normalizeCourierRules(data);
      if (rules) return rules;
    }
  } catch { /* lanjut fallback */ }
  try {
    const { data, error } = await supabase.from('products').select('attributes').eq('id', productId).maybeSingle();
    if (error || !data) return null;
    return normalizeCourierRules((data as any)?.attributes?.courierRules);
  } catch {
    return null;
  }
}

export function isBlockedCourier(rules: ProductCourierRules | null, code?: string | null): boolean {
  if (!rules || !code) return false;
  return rules.blockedLogistics.includes(code.toLowerCase());
}

// Kunci kanonik kurir (insiden 2026-10-01: daftar blokir berisi singkatan Everpro
// seperti "scp", sedangkan tarif membawa "SiCepat"/"SICEPAT" — exact-match tidak
// pernah cocok sehingga blokir diabaikan diam-diam). Samakan kedua sisi ke kunci
// ini SEBELUM dibandingkan. Tak dikenal → apa adanya (fail-open seperti dulu).
export function canonCourierKey(v: unknown): string {
  const s = String(v || '').trim().toLowerCase();
  if (!s) return '';
  // Alias lintas-agregator (insiden 2026-10-05: logo ID Express MT hilang —
  // Mengantar kirim code 'ide', Everpro kirim 'IDE'/'idexpress'/'ID Express',
  // kunci beda → borrowLogos gagal). Normalisasi alnum dulu biar
  // 'ID Express'/'IDE Express'/'idexpress'/'ide' = 1 kunci. Sama utk J&T.
  const a = s.replace(/[^a-z]/g, '');
  if (a === 'ide' || a === 'id' || (a.startsWith('id') && a.includes('express'))) return 'idexpress';
  if (a === 'jt' || a === 'jnt') return 'jnt';
  if (/sicepat|si.cepat|^scp$|gokil|siunt/.test(s)) return 'sicepat';
  if (/lion|^lnp$|regpack|jagopack|bosspack|bigpack|onepack|vippack|interpack/.test(s)) return 'lion';
  if (/idexpress|id.express|^idx$|idstd/.test(s)) return 'idexpress';
  if (/\bjne\b|^yes$|^oke$|ctc/.test(s)) return 'jne';
  if (/jnt|j&t/.test(s)) return 'jnt';
  if (/^jtc$/.test(s)) return 'jtc';
  if (/anteraja|^anter$/.test(s)) return 'anteraja';
  if (/ninja|^nxp$/.test(s)) return 'ninja';
  if (/spx|shopee/.test(s)) return 'spx';
  if (/^sap/.test(s)) return 'sap';
  if (/tiki/.test(s)) return 'tiki';
  if (/wahana|^whn$/.test(s)) return 'wahana';
  if (/rpx/.test(s)) return 'rpx';
  if (/^pos$|\bpos\b|kilat/.test(s)) return 'pos';
  if (/indah/.test(s)) return 'indah';
  if (/paxel/.test(s)) return 'paxel';
  if (/grab|^grb$/.test(s)) return 'grab';
  if (/gojek|gosend/.test(s)) return 'gojek';
  if (/borzo/.test(s)) return 'borzo';
  if (/deliveree/.test(s)) return 'deliveree';
  return s;
}

// Blokir/drop-only versi kanonik: cocok walau format kode/nama beda ("scp" vs
// "SiCepat"). Cek SEMUA identitas yang ada (code + name) — salah satu cocok = kena.
export function isBlockedCourierCanon(rules: ProductCourierRules | null, code?: unknown, name?: unknown): boolean {
  if (!rules?.blockedLogistics?.length) return false;
  const blocked = new Set(rules.blockedLogistics.map(canonCourierKey));
  return [code, name].map(canonCourierKey).some(k => !!k && blocked.has(k));
}

export function isDropOnlyCourier(rules: ProductCourierRules | null, code?: string | null): boolean {
  if (!rules || !code) return false;
  return rules.dropOnlyLogistics.includes(code.toLowerCase());
}

export function isDropOnlyCourierCanon(rules: ProductCourierRules | null, code?: unknown, name?: unknown): boolean {
  if (!rules?.dropOnlyLogistics?.length) return false;
  const dropOnly = new Set(rules.dropOnlyLogistics.map(canonCourierKey));
  return [code, name].map(canonCourierKey).some(k => !!k && dropOnly.has(k));
}
